import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PodgladSpsConfig } from '@/components/PodgladSpsConfig';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { fetchSpsConfig, zapiszSpsConfig } from '@/lib/machines-api';

/**
 * ZMIANA `SPSConfig.XML` MASZYNY — baza, serwer, numer automatu.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  DWA OSTRZEŻENIA MUSZĄ PAŚĆ PRZED KLIKNIĘCIEM, nie po. Pierwsze: zapis ZATRZYMUJE INTERFEJS
 *  automatu, więc przez chwilę maszyna nie wydaje. Drugie: zmiana numeru NIE przepisuje historii
 *  wydań, a co z nią zrobić — jest dopiero do rozstrzygnięcia.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ TREŚĆ OSTRZEŻENIA O NUMERZE PRZYCHODZI Z API, nie stoi tutaj. Ta sama treść wraca przy odmowie
 * zapisu bez potwierdzenia i ląduje w rejestrze zmian — trzy kopie tego samego zdania rozjechałyby
 * się przy pierwszej poprawce, a wtedy ekran obiecywałby co innego niż zapisuje audyt.
 */
export function SpsConfigDialog({
  maszyna,
  groupId,
  onClose,
}: {
  maszyna: { id: string; name: string };
  groupId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();

  const stan = useQuery({
    queryKey: ['spsconfig', maszyna.id, groupId],
    queryFn: () => fetchSpsConfig(maszyna.id, groupId),
  });

  const [baza, setBaza] = useState('');
  const [serwer, setSerwer] = useState('');
  const [vmid, setVmid] = useState('');
  const [rozumiem, setRozumiem] = useState(false);
  /** Podgląd całego pliku — osobne okno, bo to osobne zadanie na maszynie i osobna treść. */
  const [podglad, setPodglad] = useState(false);
  const [zapisuje, setZapisuje] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  /** Pola startują tym, co maszyna ZAMELDOWAŁA — żeby zmiana była zmianą, a nie przepisywaniem. */
  useEffect(() => {
    if (!stan.data) return;
    setBaza(stan.data.zgloszone.baza ?? '');
    setSerwer(stan.data.zgloszone.serwer ?? '');
    setVmid(stan.data.zgloszone.vmid === null ? '' : String(stan.data.zgloszone.vmid));
  }, [stan.data]);

  const d = stan.data;
  const zmienioneVmid = d !== undefined && vmid.trim() !== '' && vmid.trim() !== String(d.zgloszone.vmid ?? '');
  const zmienionaBaza = d !== undefined && baza.trim() !== (d.zgloszone.baza ?? '');
  const zmienionySerwer = d !== undefined && serwer.trim() !== (d.zgloszone.serwer ?? '');
  const cokolwiek = zmienioneVmid || zmienionaBaza || zmienionySerwer;
  const mozna = cokolwiek && (!zmienioneVmid || rozumiem) && d?.agent.online === true && d?.zadanieWToku === null;

  const zapisz = async (): Promise<void> => {
    setZapisuje(true);
    setBlad(null);
    try {
      const w = await zapiszSpsConfig(maszyna.id, {
        groupId,
        ...(zmienionaBaza ? { baza: baza.trim() } : {}),
        ...(zmienionySerwer ? { serwer: serwer.trim() } : {}),
        ...(zmienioneVmid ? { vmid: Number(vmid.trim()), rozumiemSkutkiVmid: rozumiem } : {}),
      });
      toast.success(t('spscfg.queued').replace('{n}', String(w.zmiany.length)));
      onClose();
    } catch (err) {
      setBlad(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`${t('spscfg.title')} — ${maszyna.name}`}>
        {stan.isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : d === undefined ? (
          <ModalError>{t('common.error')}</ModalError>
        ) : (
          <FormGrid>
            {/*
              ⚠️ BANER O PRZERWIE W WYDAWANIU STOI NA GÓRZE I JEST NIEZAMYKALNY. Zapis wymaga
              zatrzymania SPSUI (program czyta konfigurację przy starcie i przy wyjściu potrafi
              zapisać swoją wersję pliku), więc automat przez chwilę nie wydaje. Kto tego nie wie
              przed kliknięciem, dowie się od operatora przy maszynie.
            */}
            <div className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
              <p className="flex items-center gap-2 font-medium">
                <AlertTriangle className="size-4" />
                {t('spscfg.restartTitle')}
              </p>
              <p className="mt-1">{t('spscfg.restartBody')}</p>
            </div>

            <p className="text-xs text-muted-foreground">
              {t('spscfg.reported')
                .replace('{kiedy}', d.zgloszone.kiedy === null ? '—' : new Date(d.zgloszone.kiedy).toLocaleString())
                .replace('{vmid}', String(d.vmidWPortalu))}
            </p>

            <Field label={t('spscfg.baza')} htmlFor="spscfg-baza">
              <Input id="spscfg-baza" autoComplete="off" value={baza} onChange={(e) => setBaza(e.target.value)} />
            </Field>

            <Field label={t('spscfg.serwer')} htmlFor="spscfg-serwer">
              <Input id="spscfg-serwer" autoComplete="off" value={serwer} onChange={(e) => setSerwer(e.target.value)} />
            </Field>

            {/*
              ⚠️ NUMER AUTOMATU MA WŁASNE OSTRZEŻENIE, ODDZIELNE OD RESTARTU — i jest ono treścią
              z API, nie napisem z tego pliku. Sprawa jest DO DECYZJI, więc pole działa, ale
              wymaga świadomego zaznaczenia; potwierdzenie ląduje w rejestrze zmian.
            */}
            <Field label={t('spscfg.vmid')} htmlFor="spscfg-vmid">
              <Input
                id="spscfg-vmid"
                inputMode="numeric"
                autoComplete="off"
                value={vmid}
                onChange={(e) => setVmid(e.target.value)}
              />
            </Field>

            {zmienioneVmid && (
              <div className="rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/10 p-3 text-xs">
                <p className="font-medium">{t('spscfg.vmidWarnTitle')}</p>
                <p className="mt-1">{d.ostrzezenieVmid}</p>
                <label className="mt-2 flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={rozumiem}
                    onChange={(e) => setRozumiem(e.target.checked)}
                  />
                  <span>{t('spscfg.vmidAck')}</span>
                </label>
              </div>
            )}

            {/*
              ⚠️ WEJŚCIE DO PODGLĄDU STOI TUTAJ, A NIE OSOBNĄ IKONĄ NA LIŚCIE AUTOMATÓW — bo tu
              trafia ktoś, kto ma pytanie o TEN plik. Pola wyżej pokazują trzy wartości, które
              portal modeluje; gdy odpowiedzi w nich nie ma, następnym krokiem jest reszta pliku,
              a nie dojazd do klienta.

              ⚠️ PODPIS MÓWI „tylko odczyt" CELOWO. Okno, w którym stoi niezamykalny baner
              „zapis zatrzyma automat", uczy ostrożności — i bez tego zdania przycisk obok
              wyglądałby na kolejną rzecz, która automat zatrzyma.
            */}
            <div className="flex items-center justify-between gap-3 rounded-md border p-3">
              <p className="text-xs text-muted-foreground">{t('spscfgview.readonly')}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPodglad(true)}
                disabled={!d.agent.online}
              >
                <FileText className="size-4" />
                {t('spscfgview.action')}
              </Button>
            </div>

            {d.zadanieWToku !== null && <p className="text-xs text-[var(--warning)]">{t('spscfg.inFlight')}</p>}
            {!d.agent.online && <p className="text-xs text-[var(--warning)]">{t('spscfg.offline')}</p>}
            {blad !== null && <ModalError>{blad}</ModalError>}
          </FormGrid>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={zapisz} disabled={!mozna || zapisuje}>
            {zapisuje && <Loader2 className="size-4 animate-spin" />}
            {t('spscfg.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
      {/*
        ⚠️ DRUGIE OKNO, NIE SEKCJA W TYM. Treść pliku ma ~4 KB i własny cykl życia (zadanie na
        maszynie, termin ważności, usunięcie przy zamknięciu) — wciśnięta w formularz zapisu
        rozpychałaby go i mieszała dwie różne operacje: „patrzę" i „zmieniam".
      */}
      <PodgladSpsConfig
        maszyna={podglad ? maszyna : null}
        groupId={groupId}
        onClose={() => setPodglad(false)}
      />
    </Dialog>
  );
}
